import './Group.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import { ActionBottomSheet, Button, Header, Placeholder, ScrollView } from '$uikit';
import { useDesktop, useGroupInfo, usePosts, useTranslator, useUnitName } from '$hooks';
import { groupInfoSelector, groupsActions } from '$store/groups';
import { RootState } from '$store/rootReducer';
import {
  CreatePostForm,
  Loader,
  MetaTags,
  ModalLink,
  Post,
  Verified,
} from '$shared/components';
import {
  IcAdd28,
  IcChevron16,
  IcCopy28,
  IcDone28,
  IcEdit28,
  IcGallery28,
  IcMessage28,
  IcMore24,
  IcStatusSending16,
} from '$assets';
import { classNames, getImageSize, rnbr } from '$utils';
import { toastActions } from '$store/toast';
import { showModal } from '$navigation/helper';
import { GroupProps } from './Group.interface';
import { PUBLIC_URL } from '$shared/constants';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';

export const Group: FC<GroupProps> = (props) => {
  const { route } = useRoute();
  const groupId = props.groupId ? props.groupId : +route.params.id;
  const group = useGroupInfo(groupId);
  const groupName = useUnitName(group);
  const dispatch = useDispatch();
  const info = useSelector((root: RootState) => groupInfoSelector(root, groupId));
  const posts = usePosts(`-${groupId}`);
  const isDesktop = useDesktop();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isMoreShown, setMoreShown] = useState(false);
  const t = useTranslator();
  const hasPhoto = group?.photoLarge !== '/assets/ava_placeholder.png';

  useEffect(() => {
    dispatch(groupsActions.loadGroupInfo(groupId));
  }, [groupId]);

  const handleExpand = useCallback(() => {
    setDescriptionExpanded(!descriptionExpanded);
  }, [descriptionExpanded, setDescriptionExpanded]);

  const handlePhotoClick = useCallback(() => {
    if (hasPhoto) {
      showModal(`photos+group${group.id}+${group.photoId}`);
    }
  }, [group, hasPhoto]);

  const handleFollow = useCallback(() => {
    dispatch(groupsActions.follow(groupId));
  }, [dispatch, groupId]);

  const handleUnfollow = useCallback(() => {
    dispatch(groupsActions.unfollow(groupId));
  }, [dispatch, groupId]);

  const handleMoreAction = useCallback(
    (item) => {
      if (item !== 'share') {
        setMoreShown(false);
      }

      if (item === 'share') {
        navigator.share({
          url: location.href,
          title: '',
          text: t('group_share_text'),
        });
      } else if (item === 'copyLink') {
        copyToClipboard(location.href);
        dispatch(toastActions.setToastSuccess(t('copied')));
      } else if (item === 'edit') {
        showModal(`edit${groupId}`);
      }
    },
    [setMoreShown, dispatch, group, t],
  );

  const handleShowMoreAction = useCallback(() => {
    setMoreShown(true);
  }, [setMoreShown, group]);

  const handlePhotoSelect = useCallback(
    ({ target }) => {
      const file = target.files[0];
      getImageSize(file).then(({ width, height }) => {
        if (height <= width * 3) {
          setMoreShown(false);
          dispatch(groupsActions.uploadPhoto({ groupId, file }));
        } else {
          dispatch(toastActions.setToastFail(t('photo_validation_size_err')));
        }
      });
    },
    [groupId, setMoreShown],
  );

  const moreActions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [];

    if (group?.isAdmin) {
      items.push({ label: t('group_edit'), value: 'edit', icon: <IcEdit28 /> });
    }

    items.push({ label: t('group_copy_link'), value: 'copyLink', icon: <IcCopy28 /> });

    /* @ts-ignore */
    if (navigator.canShare) {
      items.push({ label: t('group_share'), value: 'share' });
    }

    if (group?.isAdmin) {
      items.push({
        label: t('group_change_photo'),
        value: 'changePhoto',
        icon: <IcGallery28 />,
        disabled: true,
        children: (
          <input
            type="file"
            className="Profile__upload_ava_input"
            onChange={handlePhotoSelect}
          />
        ),
      });
    }

    return items;
  }, [group, handlePhotoSelect, t]);

  const metaDescription = useMemo(() => {
    const result: string[] = [];

    if (!group) {
      return '';
    }

    if (group.description) {
      result.push(`Description: ${group.description}`);
    }

    if (group.followers > 0) {
      result.push(`Followers: ${group.followers}`);
    }

    return result.filter((item) => item.length > 0).join('. ');
  }, [group]);

  function renderPosts() {
    if (group.isBanned) {
      return null;
    }

    if (posts.length === 0) {
      return <Placeholder>{t('profile_posts_empty')}</Placeholder>;
    }

    return (
      <div>
        {posts.map((post) => (
          <Post key={post} postId={post} />
        ))}
      </div>
    );
  }

  function renderStatus() {
    if (group.isModer) {
      return (
        <ModalLink className="Profile__common_info__status" name={`status${groupId}`}>
          <IcMessage28 className="Profile__common_info__status__icon" />
          {group.status || 'Изменить статус'}
          <IcChevron16 className="Profile__common_info__status__chevron" />
        </ModalLink>
      );
    }

    if (group.status) {
      return null;
    }

    return <div className="Profile__common_info__status">{group.status}</div>;
  }

  function renderContent() {
    if (!info || (info.isLoading && !group)) {
      return <Loader isWrapped />;
    }

    if (info.isFailed) {
      return <Placeholder>{info.failedReason}</Placeholder>;
    }

    return (
      <div className="Group">
        <div className="Profile__common_info">
          <div
            onClick={handlePhotoClick}
            className={classNames('Profile__common_info__photo', {
              active: hasPhoto,
            })}
            role="img"
            aria-label={groupName}
            style={{ backgroundImage: `url(${group.photo})` }}
          />
          <div className="Profile__common_info__cont">
            <div className="Profile__common_info__name_wrap">
              <div className="Profile__common_info__name">{groupName}</div>
              <Verified objectId={-groupId} large />
            </div>
            {renderStatus()}
          </div>
        </div>

        <div>
          {group.description && (
            <div className="Group__info_row">
              <div className="Group__info_label">{t('group_description')}</div>
              <div className="Group__info_cont">
                <div
                  className={classNames({
                    Group__description: true,
                    expanded: descriptionExpanded,
                  })}
                >
                  {rnbr(group.description)}
                  <div className="Group__description__expand_btn" onClick={handleExpand}>
                    {t(
                      descriptionExpanded
                        ? 'group_description_minimize'
                        : 'group_description_expand',
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!!group.link && (
            <div className="Group__info_row">
              <div className="Group__info_label">{t('group_site')}</div>
              <div className="Group__info_cont">
                <a href={group.link} target="_blank" rel="noreferrer">
                  {group.link}
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="Profile__follow_wrap">
          {info.isFollowed ? (
            <Button icon={<IcDone28 />} mode="secondary" onClick={handleUnfollow}>
              {t('followed_button')}
            </Button>
          ) : (
            <Button icon={<IcAdd28 />} onClick={handleFollow}>
              {t('follow_button')}
            </Button>
          )}
        </div>
        <ModalLink className="Group__followers" name={`followers-${groupId}`}>
          {t('followed_count', { count: group.followers })}
        </ModalLink>
        {info.canPost && <CreatePostForm ownerId={-groupId} />}
        {info.scheduledCount > 0 && (
          <ModalLink className="Group__scheduled_posts" name={`scheduled-${groupId}`}>
            <IcStatusSending16 width={28} height={28} />
            {t('scheduled_count', { count: info.scheduledCount })}
          </ModalLink>
        )}
        {renderPosts()}
      </div>
    );
  }

  return (
    <>
      <MetaTags
        title={`${groupName} | Ton.Place | ID: ${groupId}`}
        description={metaDescription}
        image={group?.photo ?? ''}
        canonical={`https://${PUBLIC_URL}/${
          group?.domain ? group.domain : `group${group?.id}`
        }`}
      />
      <Header
        showBackButton={!isDesktop}
        rightIcons={[
          {
            icon: <IcMore24 />,
            onClick: handleShowMoreAction,
          },
        ]}
      />
      <ScrollView
        onLoadMore={() => dispatch(groupsActions.loadMorePosts({ ownerId: groupId }))}
        canLoadMore={
          info && !info.isLoading && !info.isPostsLoadingMore && !!info.postsNextFrom
        }
        isLoadingMore={info?.isPostsLoadingMore}
      >
        {renderContent()}
      </ScrollView>
      <ActionBottomSheet
        title={t('group_actions_title')}
        isOpened={isMoreShown}
        options={moreActions}
        onChange={handleMoreAction}
        onClose={() => setMoreShown(false)}
      />
    </>
  );
};
